import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Legal.css';

export const Terms: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* Header */}
      <header className="header">
        <nav className="nav-container">
          <a href="/">
            <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" />
          </a>
          
          <ul className="nav-menu">
            <li><a href="/#features">Features</a></li>
            <li><a href="/#pricing">Pricing</a></li>
            <li><a href="/#industries">Industries</a></li>
            <li><a href="/contact">Contact</a></li>
            <li><a href="/blog">Blog</a></li>
            <li>
              <button onClick={() => navigate('/login')} className="btn btn-primary">
                Try Gerbo Live
              </button>
            </li>
          </ul>
        </nav>
      </header>

      {/* Legal Content */}
      <div className="legal-container">
        <div className="legal-hero">
          <h1>Terms of Service</h1>
          <p className="legal-updated">Last Updated: February 14, 2026</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing or using Gerbo's AI receptionist services ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
            </p>
            <p>
              These Terms apply to all visitors, users, and others who access or use the Service, whether you are a business owner, employee, or authorized user of a Gerbo account.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Service Description</h2>
            <p>
              Gerbo provides an AI-powered virtual receptionist service that:
            </p>
            <ul>
              <li>Answers incoming phone calls on behalf of your business</li>
              <li>Captures caller information including names, contact details, and reasons for calling</li>
              <li>Provides call summaries via email or SMS</li>
              <li>Operates 24/7 to ensure no calls are missed</li>
              <li>Uses natural language processing to engage callers in human-like conversations</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Account Registration and Security</h2>
            <h3>3.1 Account Creation</h3>
            <p>
              To use our Service, you must create an account by providing accurate, complete, and current information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </p>
            
            <h3>3.2 Account Eligibility</h3>
            <p>
              You must be at least 18 years old and capable of forming a binding contract to use our Service. By creating an account, you represent and warrant that you meet these requirements.
            </p>
            
            <h3>3.3 Security</h3>
            <p>
              You agree to immediately notify us of any unauthorized use of your account or any other breach of security. We cannot and will not be liable for any loss or damage arising from your failure to comply with this security obligation.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Subscription Plans and Pricing</h2>
            <h3>4.1 Plan Tiers</h3>
            <p>
              Gerbo offers multiple subscription tiers (Essential, Pro, Elite) with varying call volumes and features. Current pricing is available on our website and may be changed with 30 days' notice.
            </p>
            
            <h3>4.2 Billing</h3>
            <p>
              Subscription fees are billed monthly in advance. You authorize us to charge your payment method on a recurring basis. If payment fails, we may suspend your service until payment is received.
            </p>
            
            <h3>4.3 Overages</h3>
            <p>
              If you exceed your plan's included call volume, additional calls will be charged at the per-call rate specified in your plan. You will be notified when approaching your limit.
            </p>
            
            <h3>4.4 Refunds</h3>
            <p>
              Subscription fees are non-refundable except as required by law or as explicitly stated in these Terms. We may offer refunds on a case-by-case basis at our sole discretion.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Acceptable Use Policy</h2>
            <p>
              You agree not to use the Service to:
            </p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Transmit any harmful, threatening, abusive, harassing, or defamatory content</li>
              <li>Engage in any fraudulent or deceptive practices</li>
              <li>Impersonate any person or entity</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use the Service for spam, phishing, or other malicious purposes</li>
              <li>Collect or harvest personal information without consent</li>
              <li>Reverse engineer or attempt to extract source code from the Service</li>
            </ul>
            <p>
              Violation of this policy may result in immediate termination of your account without refund.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Call Recording and Data Collection</h2>
            <h3>6.1 Call Recording</h3>
            <p>
              All calls handled by Gerbo are recorded and transcribed for quality assurance, training purposes, and to generate call summaries. By using our Service, you consent to this recording and agree to inform your callers that calls may be recorded.
            </p>
            
            <h3>6.2 Data Processing</h3>
            <p>
              We process call data, including caller information, call content, and metadata, as described in our Privacy Policy. This data is used to provide and improve the Service.
            </p>
            
            <h3>6.3 Data Retention</h3>
            <p>
              Call recordings and transcripts are retained for a minimum of 90 days and may be retained longer for compliance, legal, or operational purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Intellectual Property Rights</h2>
            <h3>7.1 Our Rights</h3>
            <p>
              The Service, including all content, features, functionality, software, and technology, is owned by Gerbo and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            
            <h3>7.2 Your License</h3>
            <p>
              Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your internal business purposes.
            </p>
            
            <h3>7.3 Your Content</h3>
            <p>
              You retain ownership of any content you provide through the Service (e.g., business information, scripts, call handling instructions). You grant us a license to use this content solely to provide the Service to you.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Service Availability and Performance</h2>
            <h3>8.1 Uptime</h3>
            <p>
              While we strive for 99.9% uptime, we do not guarantee uninterrupted or error-free service. The Service may be unavailable due to maintenance, upgrades, or circumstances beyond our control.
            </p>
            
            <h3>8.2 No Warranty</h3>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
            
            <h3>8.3 AI Limitations</h3>
            <p>
              While our AI is designed to provide professional and accurate service, it may occasionally misunderstand callers or provide incorrect information. We are not liable for any losses resulting from AI errors or limitations.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, GERBO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
            <p>
              OUR TOTAL LIABILITY FOR ANY CLAIMS UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
            </p>
            <p>
              Some jurisdictions do not allow the exclusion or limitation of certain damages, so some of the above limitations may not apply to you.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Gerbo, its affiliates, and their respective officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your content or business practices</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>11. Termination</h2>
            <h3>11.1 By You</h3>
            <p>
              You may cancel your subscription at any time through your account settings. Cancellation will take effect at the end of your current billing period.
            </p>
            
            <h3>11.2 By Us</h3>
            <p>
              We may suspend or terminate your account immediately if you violate these Terms or for any other reason at our sole discretion, with or without notice.
            </p>
            
            <h3>11.3 Effect of Termination</h3>
            <p>
              Upon termination, your right to use the Service ceases immediately. We may delete your data in accordance with our data retention policies. Provisions that should survive termination (including payment obligations, limitations of liability, and indemnification) will continue in effect.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Compliance and Regulatory</h2>
            <h3>12.1 GDPR and Data Protection</h3>
            <p>
              We comply with GDPR and SADC data protection regulations. See our Privacy Policy for details on how we process personal data.
            </p>
            
            <h3>12.2 Telecommunications Regulations</h3>
            <p>
              You are responsible for ensuring your use of the Service complies with all applicable telecommunications laws and regulations in your jurisdiction.
            </p>
            
            <h3>12.3 Call Recording Laws</h3>
            <p>
              You are responsible for complying with call recording laws in your jurisdiction, including providing appropriate notice to callers if required.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. If we make material changes, we will notify you by email or through the Service at least 30 days before the changes take effect.
            </p>
            <p>
              Your continued use of the Service after changes become effective constitutes acceptance of the modified Terms. If you do not agree to the changes, you must cancel your subscription.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Dispute Resolution</h2>
            <h3>14.1 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of South Africa, without regard to its conflict of law provisions.
            </p>
            
            <h3>14.2 Arbitration</h3>
            <p>
              Any dispute arising out of or relating to these Terms or the Service shall first be attempted to be resolved through good-faith negotiations. If negotiations fail, disputes shall be resolved through binding arbitration in accordance with the Arbitration Foundation of Southern Africa (AFSA) rules.
            </p>
            
            <h3>14.3 Exceptions</h3>
            <p>
              Either party may seek injunctive or other equitable relief in any court of competent jurisdiction to prevent actual or threatened infringement of intellectual property rights.
            </p>
          </section>

          <section className="legal-section">
            <h2>15. General Provisions</h2>
            <h3>15.1 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Gerbo regarding the Service and supersede all prior agreements.
            </p>
            
            <h3>15.2 Severability</h3>
            <p>
              If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will remain in full force and effect.
            </p>
            
            <h3>15.3 Waiver</h3>
            <p>
              Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.
            </p>
            
            <h3>15.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms or your rights under them without our prior written consent. We may assign these Terms without restriction.
            </p>
            
            <h3>15.5 Force Majeure</h3>
            <p>
              We shall not be liable for any delay or failure to perform resulting from causes beyond our reasonable control, including but not limited to acts of God, war, terrorism, riots, natural disasters, or failures of telecommunications infrastructure.
            </p>
          </section>

          <section className="legal-section">
            <h2>16. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Gerbo AI</strong></p>
              <p>Email: <a href="mailto:legal@gerboai.com">legal@gerboai.com</a></p>
              <p>Phone: <a href="tel:+27684887039">+27 68 488 7039</a></p>
              <p>Support: <a href="mailto:support@gerboai.com">support@gerboai.com</a></p>
            </div>
          </section>

          <section className="legal-section acknowledgment">
            <h2>Acknowledgment</h2>
            <p>
              BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU ARE NOT AUTHORIZED TO USE THE SERVICE.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-column">
              <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" style={{ marginBottom: '1.5rem' }} />
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1.7 }}>Your friendly AI receptionist, always available to help your business grow.</p>
            </div>

            <div className="footer-column">
              <h4>Product</h4>
              <ul className="footer-links">
                <li><a href="/#features">Features</a></li>
                <li><a href="/#pricing">Pricing</a></li>
                <li><a href="/#industries">Industries</a></li>
                <li><a href="/blog">Blog</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Company</h4>
              <ul className="footer-links">
                <li><a href="/contact">Contact</a></li>
                <li><a href="/privacy">Privacy</a></li>
                <li><a href="/terms">Terms</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Support</h4>
              <ul className="footer-links">
                <li><a href="mailto:support@gerboai.com">support@gerboai.com</a></li>
                
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2025 Gerbo. All rights reserved.</p>
            <p>Made by Solid5</p>
          </div>
        </div>
      </footer>
    </>
  );
};